import { Card, CardContent, cn, Modal } from '@crm/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { GRID_GAP } from './card-grid';

/**
 * Colunas do bloco de numeros no desktop (no celular sao sempre duas). Sao
 * atalhos sobre a grade do sistema - o espacamento e a quebra por tela vem do
 * `CardGrid`, entao mudam ali para todas as telas de uma vez.
 *
 * **Escolha a que divide a quantidade de cartoes do bloco.** Sete numeros numa
 * grade de quatro deixam tres celulas vazias no fim, e o buraco se le como
 * conteudo que faltou carregar - por isso os blocos do painel sao agrupados
 * para fechar a linha, e nao pelo assunto.
 */
export const WIDGET_GRID = {
  three: cn('grid grid-cols-2 lg:grid-cols-3', GRID_GAP),
  four: cn('grid grid-cols-2 lg:grid-cols-4', GRID_GAP),
  six: cn('grid grid-cols-2 lg:grid-cols-6', GRID_GAP),
  seven: cn('grid grid-cols-2 lg:grid-cols-7', GRID_GAP),
} as const;

interface StatCardProps {
  title: string;
  /**
   * O que o numero mede e, quando cabe, o que fazer com ele. Vira a modal que
   * abre ao tocar no cartao - o texto sai da frente e fica a um clique.
   */
  about?: { title: string; body: string[] };
  /**
   * Titulo do celular. "Agendamentos hoje" vira "Hoje": com dois cartoes por
   * linha, o rotulo longo quebra em tres linhas e afoga o numero.
   */
  shortTitle?: string;
  value: React.ReactNode;
  /** Recorte do numero - periodo, por exemplo -, numa linha abaixo dele. */
  hint?: string;
  /**
   * Mostra o simbolo de informacao ao lado do titulo. So faz sentido com
   * `about`: e o aviso de que ali existe algo a ler, para o numero cuja
   * conta nao se adivinha pelo nome.
   */
  info?: boolean;
  icon?: React.ReactNode;
  /** Quando informado, o cartao inteiro vira link para esta rota. */
  to?: string;
  className?: string;
}

/**
 * O "i" da explicacao, sem circulo em volta: a moldura pesava mais que o
 * aviso, e ao lado de um numero grande virava um segundo botao na cara.
 */
export function MarcaDeInfo({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'shrink-0 font-serif text-[0.8em] italic leading-none text-muted-foreground/60',
        className,
      )}
    >
      i
    </span>
  );
}

/**
 * Numero do painel. O icone virou marca d'agua no canto: ele situa o cartao
 * sem disputar espaco com o valor, que e o que se le de relance. O fundo
 * proprio separa o widget da tela sem precisar de moldura forte.
 */
export function StatCard({
  title,
  shortTitle,
  value,
  hint,
  info,
  about,
  icon,
  to,
  className,
}: StatCardProps) {
  const [explicando, setExplicando] = useState(false);
  /** Tocavel quando leva a algum lugar ou tem o que explicar. */
  const interativo = Boolean(to || about);

  const conteudo = (
    <Card
      className={cn(
        'relative h-full overflow-hidden rounded-card border-border-strong bg-card shadow-[0_1px_2px_0_rgba(15,23,42,0.05),0_2px_6px_-2px_rgba(15,23,42,0.10)] dark:bg-white/[0.06] dark:shadow-[0_1px_2px_0_rgba(0,0,0,0.5)]',
        interativo &&
          'transition-shadow hover:border-primary/40 hover:shadow-[0_2px_4px_0_rgba(15,23,42,0.06),0_8px_16px_-6px_rgba(15,23,42,0.16)]',
        className,
      )}
    >
      {icon ? (
        <span
          aria-hidden
          className="pointer-events-none absolute right-2 top-2 text-foreground/[0.07] dark:text-white/[0.10] [&_svg]:h-10 [&_svg]:w-10"
        >
          {icon}
        </span>
      ) : null}

      {/* Cartao mais baixo que o padrao do Card: sao muitos numeros na mesma
          tela, e cada folga a mais empurra o proximo bloco para fora. */}
      <CardContent className="relative p-3 pt-8 sm:p-4 sm:pt-9">
        {/* Uma linha so: com duas, o cartao crescia e a coluna de numeros
            perdia o alinhamento. Passa por cima do icone sem problema - ele e
            marca d'agua, nao conteudo. */}
        <span className="relative z-10 block truncate text-base font-medium text-slate-600 dark:text-slate-300">
          <span className={shortTitle ? 'sm:hidden' : undefined}>{shortTitle ?? title}</span>
          {shortTitle ? <span className="hidden sm:inline">{title}</span> : null}
        </span>
        {/* O simbolo acompanha o numero, nao o rotulo: e o valor que levanta
            a duvida de como foi calculado. */}
        <span className="mt-0.5 flex items-center gap-3 whitespace-nowrap text-lg font-semibold tracking-tight tabular-nums sm:text-xl">
          {value}
          {info && about ? <MarcaDeInfo /> : null}
        </span>
        {/* O recorte do numero, quando ele nao e obvio pelo titulo - o
            periodo cabe aqui embaixo sem inchar o rotulo. */}
        {hint ? (
          <span className="mt-0.5 block truncate text-xs text-muted-foreground tabular-nums">
            {hint}
          </span>
        ) : null}
      </CardContent>
    </Card>
  );

  if (to) {
    return (
      <Link to={to} className="block focus-visible:outline-none">
        {conteudo}
      </Link>
    );
  }

  if (about) {
    return (
      <>
        <button
          type="button"
          onClick={() => setExplicando(true)}
          className="block w-full text-left focus-visible:outline-none"
        >
          {conteudo}
        </button>

        <Modal
          open={explicando}
          onClose={() => setExplicando(false)}
          title={about.title}
          size="small"
          actions={[{ label: 'Entendi', onClick: () => setExplicando(false) }]}
        >
          <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
            {about.body.map((paragrafo) => (
              <p key={paragrafo}>{paragrafo}</p>
            ))}
          </div>
        </Modal>
      </>
    );
  }

  return conteudo;
}
